import { InvertedIndex } from "./inverted_index";
import { whitespaceTokenizer } from "./tokenizer";
import { lowerCaseFilter, trimNonWordCharactersFilter } from "./filters";
export function DEFAULT_FILTER(term) {
    return trimNonWordCharactersFilter(lowerCaseFilter(term));
}
/**
 * Document Index.
 */
var DocumentIndex = /** @class */ (function () {
    function DocumentIndex(options) {
        this._documents = new Map();
        this._index = new InvertedIndex();
        this._fields = [];
        this._tokenizer = whitespaceTokenizer;
        this._filter = DEFAULT_FILTER;
        this._bm25k1 = 1.2;
        this._bm25b = 0.75;
        if (options !== undefined) {
            if (options.tokenizer !== undefined) {
                this._tokenizer = options.tokenizer;
            }
            if (options.filter !== undefined) {
                this._filter = options.filter;
            }
            var bm25 = options.bm25;
            if (bm25 !== undefined) {
                if (bm25.k1 !== undefined) {
                    this._bm25k1 = bm25.k1;
                }
                if (bm25.b !== undefined) {
                    this._bm25b = bm25.b;
                }
            }
        }
    }
    Object.defineProperty(DocumentIndex.prototype, "size", {
        /**
         * Returns number of indexed document.
         */
        get: function () {
            return this._documents.size;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Create Field Index.
     */
    DocumentIndex.prototype.addField = function (fieldName, options) {
        var getter = fieldName;
        var boost = 1;
        if (options !== undefined) {
            if (options.getter !== undefined) {
                getter = options.getter;
            }
            if (options.boost !== undefined) {
                boost = options.boost;
            }
        }
        var details = {
            name: fieldName,
            getter: getter,
            boost: boost,
            sumLength: 0,
            avgLength: 0,
        };
        this._fields.push(details);
    };
    /**
     * Add document to the index.
     */
    DocumentIndex.prototype.add = function (documentId, document) {
        var _this = this;
        var documentTermCounts = new Array(this._fields.length);
        var termCounts = new Map();
        for (var i = 0; i < this._fields.length; i++) {
            var fieldDetails = this._fields[i];
            var fieldAccessor = fieldDetails.getter;
            var fieldValue = typeof fieldAccessor === "string" ?
                document[fieldAccessor] :
                fieldAccessor(document);
            if (fieldValue === undefined) {
                documentTermCounts[i] = 0;
            }
            else {
                // tokenize text
                var terms = this._tokenizer(fieldValue);
                // filter and count terms, ignore empty strings
                var filteredTermsCount = 0;
                for (var j = 0; j < terms.length; j++) {
                    var term = this._filter(terms[j]);
                    if (term !== "") {
                        filteredTermsCount++;
                        var counts = termCounts.get(term);
                        if (counts === undefined) {
                            counts = new Array(this._fields.length).fill(0);
                            termCounts.set(term, counts);
                        }
                        counts[i] += 1;
                    }
                }
                fieldDetails.sumLength += filteredTermsCount;
                fieldDetails.avgLength = fieldDetails.sumLength / (this._documents.size + 1);
                documentTermCounts[i] = filteredTermsCount;
            }
        }
        var docDetails = {
            docId: documentId,
            removed: false,
            fieldLengths: documentTermCounts,
        };
        this._documents.set(documentId, docDetails);
        termCounts.forEach(function (termCounters, term) {
            _this._index.add(term, docDetails, termCounters);
        });
    };
    /**
     * Remove document from the index.
     */
    DocumentIndex.prototype.remove = function (documentId) {
        var details = this._documents.get(documentId);
        if (details !== undefined) {
            details.removed = true;
            this._documents.delete(documentId);
            for (var i = 0; i < this._fields.length; i++) {
                var fieldLength = details.fieldLengths[i];
                if (fieldLength > 0) {
                    var field = this._fields[i];
                    field.sumLength -= fieldLength;
                    field.avgLength = field.sumLength / this._documents.size;
                }
            }
        }
    };
    /**
     * Search with a free text query.
     *
     * All token separators work as a disjunction operator.
     */
    DocumentIndex.prototype.search = function (query) {
        var queryTerms = this._tokenizer(query);
        var scores = new Map();
        for (var i = 0; i < queryTerms.length; i++) {
            var term = this._filter(queryTerms[i]);
            if (term !== "") {
                var expandedTerms = this._index.expandTerm(term);
                var documents = new Set();
                for (var j = 0; j < expandedTerms.length; j++) {
                    var eTerm = expandedTerms[j];
                    var expansionBoost = eTerm === term ? 1 : Math.log(1 + (1 / (1 + eTerm.length - term.length)));
                    var termNode = this._index.get(eTerm);
                    if (termNode !== null && termNode.firstPosting !== null) {
                        var documentFrequency = 0;
                        var pointer = termNode.firstPosting;
                        var prevPointer = null;
                        while (pointer !== null) {
                            if (pointer.details.removed) {
                                if (prevPointer === null) {
                                    termNode.firstPosting = pointer.next;
                                }
                                else {
                                    prevPointer.next = pointer.next;
                                }
                            }
                            else {
                                prevPointer = pointer;
                                documentFrequency++;
                            }
                            pointer = pointer.next;
                        }
                        if (documentFrequency > 0) {
                            // calculating BM25 idf
                            var idf = Math.log(1 + (this.size - documentFrequency + 0.5) / (documentFrequency + 0.5));
                            pointer = termNode.firstPosting;
                            while (pointer !== null) {
                                if (!pointer.details.removed) {
                                    var score = 0;
                                    for (var x = 0; x < pointer.details.fieldLengths.length; x++) {
                                        var tf = pointer.termFrequency[x];
                                        if (tf > 0) {
                                            // calculating BM25 tf
                                            var fieldLength = pointer.details.fieldLengths[x];
                                            var fieldDetails = this._fields[x];
                                            var avgFieldLength = fieldDetails.avgLength;
                                            var k1 = this._bm25k1;
                                            var b = this._bm25b;
                                            tf = ((k1 + 1) * tf) / (k1 * ((1 - b) + b * (fieldLength / avgFieldLength)) + tf);
                                            score += tf * idf * fieldDetails.boost * expansionBoost;
                                        }
                                    }
                                    if (score > 0) {
                                        var docId = pointer.details.docId;
                                        var prevScore = scores.get(docId);
                                        if (prevScore !== undefined && documents.has(docId)) {
                                            scores.set(docId, Math.max(prevScore, score));
                                        }
                                        else {
                                            scores.set(docId, prevScore === undefined ? score : prevScore + score);
                                        }
                                        documents.add(docId);
                                    }
                                }
                                pointer = pointer.next;
                            }
                        }
                    }
                }
            }
        }
        var result = [];
        scores.forEach(function (score, documentId) {
            result.push({
                docId: documentId,
                score: score,
            });
        });
        result.sort(function (a, b) {
            return b.score - a.score;
        });
        return result;
    };
    /**
     * Expand term with all possible combinations.
     */
    DocumentIndex.prototype.expandTerm = function (term) {
        return this._index.expandTerm(term);
    };
    /**
     * Convert query to an array of terms.
     */
    DocumentIndex.prototype.queryToTerms = function (query) {
        var result = [];
        var tokens = this._tokenizer(query);
        for (var i = 0; i < tokens.length; i++) {
            var term = this._filter(tokens[i]);
            if (term !== "") {
                result = result.concat(this._index.expandTerm(term));
            }
        }
        return result;
    };
    /**
     * Remove outdated/removed documents from the index.
     */
    DocumentIndex.prototype.vacuum = function () {
        this._index.vacuum();
    };
    return DocumentIndex;
}());
export { DocumentIndex };
//# sourceMappingURL=document_index.js.map